import React, { useState, useCallback, useMemo } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useChat } from '@/hooks/useChat';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import IconButton from '@/components/Buttons/IconButton';
import ChatDropdown from '@/components/Dropdowns/ChatDropdown';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, isXS, isLastItem, isSelected) => StyleSheet.create({
  // Non-touchable row container. Splitting the content tap area and the
  // ellipsis trigger into sibling touchables (instead of nesting them) is
  // what lets zeego's native UIMenu / Material menu open reliably on
  // iOS / Android — nested Pressables steal the touch before zeego ever
  // sees it.
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: !isLastItem ? StyleSheet.hairlineWidth : 0,
    borderBottomColor: colors.border_color_secondary,
    borderRadius: BORDER_RADIUS.small,
    backgroundColor: isSelected ? `${colors.accent_color_primary}1A` : 'transparent',
  },
  contentPressable: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: BORDER_RADIUS.small,
  },
  contentPressableHovered: {
    backgroundColor: isSelected
      ? `${colors.accent_color_primary}26`
      : colors.solid_color_tertiary,
  },
  checkbox: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
    minWidth: 0,
  },
  pinIcon: {
    flexShrink: 0,
  },
  title: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  date: {
    flexShrink: 0,
    marginLeft: 8,
  },
  statusIcon: {
    marginLeft: 2,
    flexShrink: 0,
  },
  subtitle: {
    lineHeight: 20,
  },
  ellipsisSlot: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});

function CollectionChatItem({
  chat,
  isLastItem = false,
  isSelectingMultiple = false,
  isSelected = false,
  onToggleSelect = () => {},
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // useChat gives us live pin/archive state and ChatDropdown wires its
  // own actions (rename / delete / archive / share / etc.) through the same
  // hook keyed on conversationId.
  const { isPinned } = useChat(chat?.id);

  const styles = useMemo(
    () => createStyles(colors, isXS, isLastItem, isSelected),
    [colors, isXS, isLastItem, isSelected],
  );

  const lastMessage = useMemo(() => {
    if (!Array.isArray(chat?.messages) || chat.messages.length === 0) return null;
    return chat.messages[chat.messages.length - 1];
  }, [chat?.messages]);

  const lastMessageContent = lastMessage?.content || t('pages.collections.noMessages');
  const chatDate = useMemo(() => {
    const raw = lastMessage?.timestamp || chat?.updatedAt || chat?.createdAt;
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    return date.toLocaleDateString(undefined, date.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
  }, [lastMessage?.timestamp, chat?.updatedAt, chat?.createdAt]);

  const handleToggleSelect = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onToggleSelect?.(chat?.id);
  }, [chat?.id, onToggleSelect]);

  const handlePress = useCallback(() => {
    if (isDropdownOpen) return;
    if (isSelectingMultiple) {
      onToggleSelect?.(chat?.id);
      return;
    }
    if (chat?.href) {
      router.push(chat.href);
    } else if (chat?.id) {
      router.push(`/c/${chat.id}`);
    }
  }, [isDropdownOpen, isSelectingMultiple, onToggleSelect, chat?.href, chat?.id, router]);

  const titleNode = (
    <View style={styles.titleRow}>
      {isPinned && (
        <Icon
          name="star"
          size={12}
          solid
          color={colors.text_color_primary}
          style={styles.pinIcon}
        />
      )}
      <Text
        size="l"
        weight="medium"
        color="primary"
        numberOfLines={1}
        style={styles.title}
      >
        {chat?.title || 'Untitled'}
      </Text>
      {(chat?.flags?.isCoworkerSession || chat?.coworkerSession) && (
        <Icon
          name="briefcase"
          size={12}
          color={colors.text_color_tertiary}
          style={styles.statusIcon}
          accessibilityLabel={t('chatInput.coworkerMode.coworker')}
        />
      )}
      {chat?.isArchived && (
        <Icon
          name="box-archive"
          size={12}
          color={colors.text_color_tertiary}
          style={styles.statusIcon}
        />
      )}
      {chat?.visibility === 'public' && (
        <Icon
          name="globe"
          size={12}
          color={colors.text_color_tertiary}
          style={styles.statusIcon}
        />
      )}
      {!!chatDate && (
        <Text size="xs" color="tertiary" style={styles.date}>
          {chatDate}
        </Text>
      )}
    </View>
  );

  const rightSlot = isSelectingMultiple ? (
    <Pressable
      onPress={handleToggleSelect}
      style={styles.checkbox}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: isSelected }}
      accessibilityLabel={`Select ${chat?.title || 'chat'}`}
      hitSlop={6}
    >
      <Icon
        name={isSelected ? 'square-check' : 'square'}
        size={20}
        solid={isSelected}
        color={isSelected ? colors.accent_color_primary : colors.text_color_secondary}
      />
    </Pressable>
  ) : (
    // CRITICAL: ChatDropdown must own the DropdownMenu.Root (no `noRoot`)
    // and we must hand it the ellipsis as `TriggerComponent`.
    //
    // zeego's iOS Root inspects its JSX children for <Trigger> and
    // <Content> via `pickChildren` — it does NOT traverse into custom
    // components or what they render to. If we put <ChatDropdown noRoot>
    // inside our own <Root>, zeego sees a custom component (not <Content>)
    // and configures the native UIMenu with zero items, so tapping looks
    // like a no-op. Letting ChatDropdown own the Root means <Trigger> and
    // <Content> are real direct children that zeego can find. Same path
    // _layout.ios.jsx uses for the iOS drawer chats list.
    <View style={styles.ellipsisSlot}>
      <ChatDropdown
        conversationId={chat?.id}
        withSelectMultipleChats={false}
        onOpenChange={setIsDropdownOpen}
        TriggerComponent={() => (
          <IconButton
            icon="ellipsis"
            iconSize={16}
            iconColor={colors.text_color_secondary}
            size="small"
            tooltip={t('common.more')}
          />
        )}
      />
    </View>
  );

  // Sibling touchables in a non-touchable row container: tapping the
  // content area navigates / toggles selection, tapping the ellipsis opens
  // the native menu. Nothing nests Pressables, so neither platform's touch
  // system can swallow events meant for the other.
  return (
    <View
      style={styles.container}
      accessibilityState={{ selected: isSelected }}
    >
      <Pressable
        onPress={handlePress}
        style={({ hovered }) => [
          styles.contentPressable,
          hovered && styles.contentPressableHovered,
        ]}
        accessibilityRole="button"
        accessibilityLabel={`Chat: ${chat?.title || ''}`}
      >
        <View style={styles.content}>
          {titleNode}
          <Text size="n" color="secondary" numberOfLines={1} style={styles.subtitle}>
            {lastMessageContent}
          </Text>
        </View>
      </Pressable>
      {rightSlot}
    </View>
  );
}

CollectionChatItem.propTypes = {
  chat: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    href: PropTypes.string,
    messages: PropTypes.array,
    isArchived: PropTypes.bool,
    visibility: PropTypes.string,
    coworkerSession: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    flags: PropTypes.shape({
      isCoworkerSession: PropTypes.bool,
    }),
  }).isRequired,
  isLastItem: PropTypes.bool,
  isSelectingMultiple: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};

export default React.memo(CollectionChatItem);
